import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { EventEmitterService } from "src/app/Services/event-emitter.service";
import { FrontlineOpsService } from "src/app/Services/frontline-ops.service";
import { BundleEditorLauncherService } from "src/app/Services/bundle-editor-launcher.service";
import { ContentActivityPanelComponent } from "../../content-activity-panel/content-activity-panel.component";
import {
  ApproveOptionsDialogComponent,
  ApproveOptionsDialogData,
  ApproveOptionsDialogResult,
} from "./approve-options-dialog/approve-options-dialog.component";
import {
  PromotePackConfirmDialogComponent,
  PromotePackConfirmDialogData,
} from "src/app/shared/components/promote-pack-confirm-dialog/promote-pack-confirm-dialog.component";

@Component({
  selector: "app-content-approvals",
  templateUrl: "./content-approvals.component.html",
  styleUrls: ["./content-approvals.component.scss"],
})
export class ContentApprovalsComponent implements OnInit {
  loading = false;
  busyKey: string | null = null;
  items: any[] = [];
  filteredItems: any[] = [];
  /** All | Approval | Promote */
  kindFilter: "All" | "Approval" | "Promote" = "All";
  toogleMenuClass = false;
  /** Reject reasons typed per row, keyed by content row key. */
  comments: Record<string, string> = {};

  constructor(
    private frontlineOps: FrontlineOpsService,
    private toastr: ToastrService,
    private router: Router,
    private dialog: MatDialog,
    private eventEmitterService: EventEmitterService,
    private bundleEditorLauncher: BundleEditorLauncherService
  ) {}

  ngOnInit(): void {
    this.eventEmitterService.setDashboardMenuStyleProperty(
      "content-approvals,Publishing Approvals"
    );
    this.refresh();
  }

  refresh(): void {
    this.loading = true;
    this.frontlineOps.listPendingApprovals().subscribe({
      next: (rows) => {
        this.items = rows || [];
        this.applyFilter();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.items = [];
        this.filteredItems = [];
        this.toastr.error(err?.error?.error || "Could not load pending approvals");
      },
    });
  }

  applyFilter(): void {
    if (this.kindFilter === "Promote") {
      this.filteredItems = this.items.filter((i) => this.isPromoteRequest(i));
    } else if (this.kindFilter === "Approval") {
      this.filteredItems = this.items.filter((i) => !this.isPromoteRequest(i));
    } else {
      this.filteredItems = [...this.items];
    }
  }

  onKindFilterChange(): void {
    this.applyFilter();
  }

  get pendingCount(): number {
    return this.filteredItems.length;
  }

  get promoteCount(): number {
    return this.items.filter((i) => this.isPromoteRequest(i)).length;
  }

  key(item: any): string {
    return item?.flowRowKey || item?.FlowRowKey || "";
  }

  /** A promote request and a review can both be listed for the same content; keep their UI state apart. */
  uiKey(item: any): string {
    const key = this.key(item);
    return key && this.isPromoteRequest(item) ? `${key}:promote` : key;
  }

  isPack(item: any): boolean {
    const type = String(item?.contentType || item?.ContentType || "").toLowerCase();
    return type === "guidepack" || type === "bundle";
  }

  isPromoteRequest(item: any): boolean {
    const kind = String(item?.requestKind || item?.RequestKind || "").toLowerCase();
    return kind === "promotetopublic";
  }

  typeLabel(item: any): string {
    if (this.isPromoteRequest(item)) {
      return this.isPack(item) ? "Promote pack" : "Promote guide";
    }
    return this.isPack(item) ? "Guide pack" : "Guide";
  }

  submittedBy(item: any): string {
    return item?.submittedByName || item?.SubmittedByName || "";
  }

  submittedByEmail(item: any): string {
    return item?.submittedByEmail || item?.SubmittedByEmail || "";
  }

  openGuide(item: any): void {
    const key = this.key(item);
    if (!key) return;
    if (this.isPack(item)) {
      const email = JSON.parse(localStorage.getItem("LoggedInUser") || "null")?.emailAddress || "";
      this.busyKey = key;
      this.bundleEditorLauncher.openByRowKey(key, email, this.router.url).subscribe((opened) => {
        this.busyKey = null;
        if (!opened) this.toastr.error("Couldn't open this guide pack. Please try again.");
      });
      return;
    }
    this.router.navigate(["/create-guide"], {
      queryParams: { flowId: key, edit: true },
    });
  }

  openHistory(item: any): void {
    const key = this.key(item);
    if (!key) return;
    ContentActivityPanelComponent.open(this.dialog, {
      contentType: this.isPack(item) ? "guidePack" : "guide",
      contentId: key,
      title: item.title || item.Title,
    });
  }

  scheduledAt(item: any): Date | null {
    const raw: string | undefined = item?.scheduledApprovalUtc || item?.ScheduledApprovalUtc;
    if (!raw) return null;
    const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
    const at = new Date(hasZone ? raw : `${raw}Z`);
    return Number.isNaN(at.getTime()) ? null : at;
  }

  scheduledBy(item: any): string {
    return item?.scheduledByEmail || item?.ScheduledByEmail || "";
  }

  approve(item: any): void {
    const key = this.key(item);
    if (!key) return;
    if (this.isPromoteRequest(item)) {
      if (this.isPack(item)) {
        this.confirmPackPromotion(item);
      } else {
        this.runApprove(item, "promoteToPublic");
      }
      return;
    }

    this.dialog
      .open<ApproveOptionsDialogComponent, ApproveOptionsDialogData, ApproveOptionsDialogResult>(
        ApproveOptionsDialogComponent,
        {
          panelClass: "approve-options-pane",
          width: "480px",
          maxWidth: "94vw",
          autoFocus: false,
          data: { title: item.title || item.Title, typeLabel: this.typeLabel(item) },
        }
      )
      .afterClosed()
      .subscribe((result) => {
        if (!result) return;
        if (result.mode === "schedule" && result.scheduledApprovalUtc) {
          this.runApprove(item, "scheduleApprove", result.scheduledApprovalUtc);
        } else {
          this.runApprove(item, "approve");
        }
      });
  }

  /** A pack goes public with the organization guides inside it, so the approver confirms that list. */
  private confirmPackPromotion(item: any): void {
    const key = this.key(item);
    const uiKey = this.uiKey(item);
    this.busyKey = uiKey;
    this.frontlineOps.getPackPublicContents(key).subscribe({
      next: (items) => {
        this.busyKey = null;
        const list = items || [];
        this.dialog
          .open<PromotePackConfirmDialogComponent, PromotePackConfirmDialogData, boolean>(
            PromotePackConfirmDialogComponent,
            {
              width: "520px",
              maxWidth: "94vw",
              autoFocus: false,
              data: { packTitle: item.title || item.Title || "", items: list, mode: "promote" },
            }
          )
          .afterClosed()
          .subscribe((confirmed) => {
            if (!confirmed) return;
            const count = list.filter((i) => i.status !== "alreadyPublic").length;
            this.runApprove(item, "promoteToPublic", undefined, {
              confirmIncludedContent: true,
              successMessage: count
                ? `Pack and ${count} item${count === 1 ? "" : "s"} inside are now public`
                : undefined,
            });
          });
      },
      error: (err) => {
        this.busyKey = null;
        this.toastr.error(err?.error?.error || "Couldn't load the guides in this pack. Please try again.");
      },
    });
  }

  private runApprove(
    item: any,
    action: "approve" | "promoteToPublic" | "scheduleApprove",
    scheduledApprovalUtc?: string,
    options: { confirmIncludedContent?: boolean; successMessage?: string } = {}
  ): void {
    const key = this.key(item);
    if (!key) return;
    const uiKey = this.uiKey(item);
    this.busyKey = uiKey;
    this.frontlineOps
      .approvalAction({
        flowRowKey: key,
        action,
        contentType: this.isPack(item) ? "guidePack" : "guide",
        comment: this.comments[uiKey]?.trim() || undefined,
        scheduledApprovalUtc,
        confirmIncludedContent: options.confirmIncludedContent,
      })
      .subscribe({
        next: () => {
          this.busyKey = null;
          delete this.comments[uiKey];
          if (action === "promoteToPublic") {
            this.toastr.success(options.successMessage || "Promoted to public under the organization");
          } else if (action === "scheduleApprove") {
            this.toastr.success(
              `Approval scheduled for ${new Date(scheduledApprovalUtc!).toLocaleString()}`
            );
          } else {
            this.toastr.success("Approved and published in your organization");
          }
          this.refresh();
        },
        error: (err) => {
          this.busyKey = null;
          this.toastr.error(
            err?.error?.error ||
              (action === "scheduleApprove" ? "Could not schedule approval" : "Approve failed")
          );
        },
      });
  }

  cancelSchedule(item: any): void {
    const key = this.key(item);
    if (!key) return;
    const uiKey = this.uiKey(item);
    this.busyKey = uiKey;
    this.frontlineOps
      .approvalAction({
        flowRowKey: key,
        action: "cancelSchedule",
        contentType: this.isPack(item) ? "guidePack" : "guide",
        comment: this.comments[uiKey]?.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.busyKey = null;
          this.toastr.success("Scheduled approval cancelled");
          this.refresh();
        },
        error: (err) => {
          this.busyKey = null;
          this.toastr.error(err?.error?.error || "Could not cancel the schedule");
        },
      });
  }

  reject(item: any): void {
    const key = this.key(item);
    if (!key) return;
    const uiKey = this.uiKey(item);
    const reason = this.comments[uiKey]?.trim();
    if (!reason) {
      this.toastr.warning("Add a short reason so the author knows what to change.");
      return;
    }
    this.busyKey = uiKey;
    const promote = this.isPromoteRequest(item);
    this.frontlineOps
      .approvalAction({
        flowRowKey: key,
        action: promote ? "cancelPromote" : "reject",
        contentType: this.isPack(item) ? "guidePack" : "guide",
        comment: reason,
      })
      .subscribe({
        next: () => {
          this.busyKey = null;
          delete this.comments[uiKey];
          this.toastr.success(promote ? "Promote request cancelled" : "Rejected");
          this.refresh();
        },
        error: (err) => {
          this.busyKey = null;
          this.toastr.error(err?.error?.error || "Reject failed");
        },
      });
  }
}
